import type { Category, Todo } from "../../domain/entities.ts";
import { createInlineBoardTask } from "./inline-board-task-controller.ts";
import { parseComposerTitle } from "./quick-capture-controller.ts";
import { revealComposerGroup, updateComposerDateError, updateComposerSummaries } from "./task-form-controller.ts";
import { updateComposerColumnField } from "./task-composer-placement.ts";

export interface TaskComposerInteractionCapabilities {
  updateCustomFields?(form: HTMLFormElement): void;
  activeRoot(): string | null;
  editable(): boolean;
  categories(): Category[];
  tasks(): Todo[];
  emptyDraft(): Todo;
  create(draft: Todo, category: Category): Promise<Todo>;
  nextOccurrence(todo: Todo): Promise<unknown>;
  openEditor(todo: Todo): void;
  closeInlineCapture(): void;
  render(): Promise<void>;
  wipExceeded(todo: Todo): boolean;
  markLocal(): void;
  sync(): void;
  toast(message: string, error?: boolean): void;
  today(): string;
  addDays(date: string, days: number): string;
  updateRecurrence(form: HTMLFormElement): void;
  formatDate(value: string): string;
  validateTitle(form: HTMLFormElement): void;
  scheduleRecovery(form: HTMLFormElement): void;
  reminderDefaults(rootId: string): number[];
}

export function createTaskComposerInteractionController(capabilities: TaskComposerInteractionCapabilities) {
  async function createInline(form: HTMLFormElement, openEditor: boolean): Promise<void> {
    await createInlineBoardTask(form, openEditor, {
      activeRoot: capabilities.activeRoot, editable: capabilities.editable, categories: capabilities.categories, tasks: capabilities.tasks,
      emptyDraft: capabilities.emptyDraft, create: capabilities.create, nextOccurrence: capabilities.nextOccurrence,
      openEditor: capabilities.openEditor, closeCapture: capabilities.closeInlineCapture, render: capabilities.render,
      wipExceeded: capabilities.wipExceeded, markLocal: capabilities.markLocal, sync: capabilities.sync, toast: capabilities.toast,
      today: capabilities.today, addDays: capabilities.addDays,
    });
  }

  function updateSummaries(form: HTMLFormElement): void {
    updateComposerSummaries(form, capabilities.formatDate);
  }

  function parseTitle(): void {
    const form = document.querySelector<HTMLFormElement>("#detail-form");
    if (form) parseComposerTitle(form, { updateRecurrence: capabilities.updateRecurrence, updateSummaries, collectionChanged: (targetForm, select) => change(targetForm, select, true) });
  }

  function revealGroup(id: string): void {
    const form = document.querySelector<HTMLFormElement>("#detail-form");
    if (form) revealComposerGroup(form, id);
  }

  function input(form: HTMLFormElement): void {
    updateSummaries(form);
    updateComposerDateError(form);
    capabilities.validateTitle(form);
    capabilities.scheduleRecovery(form);
  }

  function change(form: HTMLFormElement, target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, quiet = false): void {
    if (target.name === "scheduleMode") {
      const floating = target.value === "floating";
      const zone = form.querySelector<HTMLElement>("[data-task-time-zone]");
      if (zone) zone.hidden = !floating;
      const hint = form.querySelector<HTMLElement>("[data-task-time-hint]");
      if (hint) hint.textContent = floating ? "Collaborators see the same task-zone clock time; their local time may differ." : "Fixed moments stay at the same instant; their local clock display changes when traveling.";
    }
    if (target.name === "categoryId" && form.dataset.placementCategory !== target.value) {
      const category = capabilities.categories().find(item => item.id === target.value);
      const removed = updateComposerColumnField(form, category);
      form.dataset.placementCategory = target.value;
      form.querySelector(".composer-placement")?.classList.toggle("has-column", !!category?.boardColumns?.length);
      if (removed.length && !quiet) capabilities.toast("Column-name tags are managed by the Column field.");
      capabilities.scheduleRecovery(form);
      capabilities.updateCustomFields?.(form);
    }
    if (form.dataset.mode === "create" && (target.name === "dueDate" || target.name === "startAt") && !form.dataset.reminderDefaultsConsidered) {
      form.dataset.reminderDefaultsConsidered = "true";
      if (target.value && !form.querySelector<HTMLInputElement>("[name=reminderOffsets]:checked") && !form.querySelector<HTMLInputElement>("[name=exactReminderAt]")?.value) {
        const defaults = new Set(capabilities.reminderDefaults(capabilities.activeRoot() || ""));
        form.querySelectorAll<HTMLInputElement>("[name=reminderOffsets]").forEach(item => { item.checked = defaults.has(Number(item.value)); });
      }
    }
    updateSummaries(form);
  }

  return { createInline, parseTitle, revealGroup, updateSummaries, input, change };
}
